import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useActions } from "../context/ActionsContext";
import { PageHeader, StatCard, TableWrap, Th, Td, Loading, EmptyState, Badge, exportCSV } from "../components/shared";
import { formatINR, formatDate, numberToWords } from "../lib/format";
import { Button } from "../components/ui/button";
import { ArrowLeft, Printer, Download, Receipt, HandCoins, Undo2, TrendingUp } from "lucide-react";

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open } = useActions();
  const [cand, setCand] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([api.get(`/candidates/${id}`), api.get(`/candidates/${id}/ledger`)]);
      setCand(c.data); setLedger(l.data);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  if (loading || !cand) return <Loading />;
  const s = cand.summary;

  const printStatement = () => window.print();
  const doExport = () => exportCSV(`${cand.candidate_code}_statement.csv`, ledger.rows.map((r) => ({
    Date: formatDate(r.txn_date), Receipt: r.receipt_number || r.txn_number, Description: r.description,
    Debit: r.debit, Credit: r.credit, Balance: r.running, Mode: r.payment_mode || "", Account: r.account_name || "",
  })));

  return (
    <div>
      <div className="no-print">
        <button onClick={() => navigate("/candidates")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-4 w-4" /> Back to Candidates</button>
        <PageHeader title={cand.name} subtitle={`${cand.candidate_code} · ${cand.mobile || "no mobile"} · ${cand.country || ""}`}
          actions={<div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => open("receive", { candidate_id: id })} data-testid="cd-receive" className="gap-1.5"><Receipt className="h-3.5 w-3.5" /> Receive</Button>
            <Button size="sm" variant="outline" onClick={() => open("advance_candidate", { candidate_id: id })} data-testid="cd-advance" className="gap-1.5"><HandCoins className="h-3.5 w-3.5" /> Advance</Button>
            <Button size="sm" variant="outline" onClick={() => open("recognize", { candidate_id: id })} data-testid="cd-recognize" className="gap-1.5"><TrendingUp className="h-3.5 w-3.5" /> Recognize</Button>
            <Button size="sm" variant="outline" onClick={() => open("refund", { candidate_id: id })} data-testid="cd-refund" className="gap-1.5"><Undo2 className="h-3.5 w-3.5" /> Refund</Button>
          </div>} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <StatCard label="Total Fee" value={s.total_fee} accent="slate" testid="cd-total-fee" />
          <StatCard label="Received" value={s.total_received} accent="emerald" testid="cd-total-received" />
          <StatCard label="Due from Candidate" value={s.due_from_candidate} accent="amber" testid="cd-due" />
          <StatCard label="Advance Held" value={s.advance_held} accent="violet" testid="cd-advance-held" />
          <StatCard label="Recognized Income" value={s.recognized_income} accent="blue" testid="cd-income" />
          <StatCard label="Paid to Agencies" value={s.paid_to_agencies} accent="rose" testid="cd-paid-agencies" />
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm mb-5">
          <h3 className="font-heading font-semibold text-sm mb-3">Candidate Cost Sheet</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[11px] text-slate-500">Fee Charged</div><div className="font-mono font-semibold" data-testid="cs-fee">{formatINR(s.total_fee)}</div></div>
            <div><div className="text-[11px] text-slate-500">Recognized Income</div><div className="font-mono font-semibold text-blue-600">{formatINR(s.recognized_income)}</div></div>
            <div><div className="text-[11px] text-slate-500">Paid to Agencies</div><div className="font-mono font-semibold text-rose-600">{formatINR(s.paid_to_agencies)}</div></div>
            <div><div className="text-[11px] text-slate-500">Gross Margin (Fee − Paid)</div><div className={`font-mono font-bold ${s.total_fee - s.paid_to_agencies >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="cs-margin">{formatINR(s.total_fee - s.paid_to_agencies)}</div></div>
          </div>
        </div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold text-slate-700 dark:text-slate-200">Candidate Ledger · Balance {formatINR(ledger.balance)}</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={doExport} className="gap-1.5"><Download className="h-3.5 w-3.5" /> CSV</Button>
            <Button size="sm" variant="outline" onClick={printStatement} className="gap-1.5"><Printer className="h-3.5 w-3.5" /> Print</Button>
          </div>
        </div>
        {ledger.rows.length === 0 ? <EmptyState title="No transactions yet" /> : (
          <TableWrap>
            <thead><tr>
              <Th>Date</Th><Th>Receipt / Txn</Th><Th>Description</Th><Th>Mode</Th><Th>Account</Th>
              <Th className="text-right">Debit</Th><Th className="text-right">Credit</Th><Th className="text-right">Balance</Th><Th>By</Th>
            </tr></thead>
            <tbody>
              {ledger.rows.map((r) => (
                <tr key={r.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 ${r.reversed ? "opacity-60" : ""}`}>
                  <Td>{formatDate(r.txn_date)}</Td>
                  <Td className="font-mono">{r.receipt_number || r.txn_number}</Td>
                  <Td>{r.description || r.payment_type}</Td>
                  <Td>{r.payment_mode || "-"}</Td><Td>{r.account_name || "-"}</Td>
                  <Td className="text-right font-mono text-rose-600">{r.debit ? formatINR(r.debit) : "-"}</Td>
                  <Td className="text-right font-mono text-emerald-600">{r.credit ? formatINR(r.credit) : "-"}</Td>
                  <Td className="text-right font-mono font-semibold">{formatINR(r.running)}</Td>
                  <Td>{r.created_by_name}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </div>

      {/* Printable statement */}
      <div className="print-area hidden print:block p-6">
        <h1 className="text-xl font-bold uppercase">PRABHAT PROFESSIONAL CONSULTANCY SERVICES PVT. LTD.</h1>
        <p className="text-xs">Main Office - Jamnagar · Branch - Vagheshwari Plot, Porbandar</p>
        <h2 className="text-lg font-semibold mt-4">Candidate Statement — {cand.name} ({cand.candidate_code})</h2>
        <table className="w-full border-collapse border border-slate-400 text-xs mt-3">
          <thead><tr>{["Date", "Ref", "Description", "Debit", "Credit", "Balance"].map((h) => <th key={h} className="border border-slate-400 px-2 py-1 text-left">{h}</th>)}</tr></thead>
          <tbody>{ledger.rows.map((r) => (
            <tr key={r.id}><td className="border border-slate-400 px-2 py-1">{formatDate(r.txn_date)}</td>
              <td className="border border-slate-400 px-2 py-1">{r.receipt_number || r.txn_number}</td>
              <td className="border border-slate-400 px-2 py-1">{r.description}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{r.debit ? formatINR(r.debit) : ""}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{r.credit ? formatINR(r.credit) : ""}</td>
              <td className="border border-slate-400 px-2 py-1 text-right">{formatINR(r.running)}</td></tr>
          ))}</tbody>
        </table>
        <p className="mt-3 font-semibold">Closing Balance: {formatINR(ledger.balance)} ({numberToWords(ledger.balance)})</p>
      </div>
    </div>
  );
}
